import { Divider, Skeleton, Stack, useMatches } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { DataGrid, PerformanceChart } from "@/components/elements";
import { defaultCurrency } from "@/lib/currency";
import { usePortfolio } from "@/services/portfolio/get-portfolio";
import { usePortfolioPerformance } from "@/services/portfolio/get-portolio-performance";
import { formatCurrency } from "@/utils/formatEuro";
import { tooltipFormatter } from "@/utils/tooltipFormatter";

import type { LabelValueProps } from "@/components/elements";
import type { Currency } from "@/types/currency";
import type { DatesRange } from "@/types/entity";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type PortfolioPerformanceProps = MantineStyleProps;

export const PortfolioPerformance: FC<PortfolioPerformanceProps> = ({ ...props }) => {
  const { t } = useTranslation();
  const [range, setRange] = useState<DatesRange>();
  const { data: portfolio, isLoading } = usePortfolio();
  const { data, isFetching, isRefetching } = usePortfolioPerformance(range);

  /* Media queries */
  const isMobile = useMatches({ base: true, sm: false });
  const fontSize = { base: "sm", sm: "sm" };
  const size = isMobile ? "sm" : "lg";

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const dataGridItems: LabelValueProps[] = [
    {
      label: t("dashboard.portfolio.value"),
      value: formatCurrency(portfolio?.value, currency.code),
      size: size,
      fz: fontSize,
      infoIcon: t("tooltips.portfolioValue"),
    },
    {
      label: t("dashboard.portfolio.garage"),
      value: portfolio?.num_garages || "-",
      size: size,
      fz: fontSize,
    },
    {
      label: t("dashboard.portfolio.cars"),
      value: portfolio?.num_cars || "-",
      size: size,
      fz: fontSize,
    },
    {
      label: t("dashboard.portfolio.investments"),
      value: formatCurrency(portfolio?.investment_capital, currency.code),
      size: size,
      fz: fontSize,
      infoIcon: t("tooltips.investedCapital"),
    },
  ];

  return (
    <Stack {...props}>
      <Skeleton visible={isLoading}>
        <DataGrid title="" items={dataGridItems} />
      </Skeleton>
      <Divider />
      <Skeleton visible={isFetching}>
        <PerformanceChart
          performance={data?.performance}
          benchmarks={[]}
          loading={isRefetching}
          onTimeframeChange={setRange}
          tooltipFormatter={tooltipFormatter}
          restrictKpisByPlan={false}
        />
      </Skeleton>
    </Stack>
  );
};
